import { capture } from '@parcel/analytics/browser';
import { feedRecordKey } from '@parcel/types';
import { Button, toast } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import { useAuth } from '../../lib/auth.js';
import {
  type DeepLinkEvent,
  type DeepLinkState,
  INITIAL_DEEP_LINK_STATE,
  decideDeepLink,
  isDeadDeepLinkStatus,
} from '../../lib/deep-link.js';
import type { FeedSearch } from '../../lib/feed-search.js';
import {
  type FeedRow as FeedRowData,
  summariesPending,
  useFeedRows,
  useFeedSeenMutations,
  useFeedStats,
  useSuggestions,
} from '../../lib/feed.js';
import { useOpenSpecChat, useSpecChatAvailable } from '../../lib/open-spec-chat.js';
import type { ProjectRow } from '../../lib/projects-display.js';
import { apiProjectToRow, useProjectDetail } from '../../lib/projects.js';
import { useRecordDetail } from '../../lib/record-detail.js';
import { useScores } from '../../lib/scores.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import {
  getStructuredAuthMetadataProfileFallback,
  getUserProfile,
  getUserProfileNameSource,
  getWorkspace,
  getWorkspaceSlugFromPath,
} from '../../lib/workspaces.js';
import { getWorkspaceScopedPath } from '../app-sidebar-nav.js';
import { FeedEmpty } from './feed-empty.js';
import { FeedFilters } from './feed-filters.js';
import { askSpecStarter, feedFirstName, specChatTitle } from './feed-format.js';
import { FeedHeader } from './feed-header.js';
import { FeedRowExpanded } from './feed-row-expanded.js';
import { FeedRow } from './feed-row.js';
import { FeedSkeleton } from './feed-skeleton.js';
import { FeedSuggested, SuggestedSkeleton } from './feed-suggested.js';
import { useFeedKeyboard } from './use-feed-keyboard.js';

/** While summaries are being written, the page reads again this often, at most this many times a visit. */
const SUMMARY_REFRESH_MS = 8_000;
const SUMMARY_REFRESHES = 3;

export function FeedPage() {
  const search = useSearch({ strict: false }) as FeedSearch;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session } = useAuth();
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const profileQuery = useQuery({ queryKey: ['me'], queryFn: getUserProfile });
  const workspaceId = workspaceQuery.data?.id;
  const linkFor = (to: `/${string}`) => getWorkspaceScopedPath(pathname, to);

  const rowsQuery = useFeedRows(workspaceId);
  const scores = useScores(workspaceId).data;
  const statsQuery = useFeedStats(workspaceId);
  // Suggested reads beside the rows, not after them; the cards mount once the rows have loaded.
  useSuggestions(workspaceId);
  const seen = useFeedSeenMutations(workspaceId);
  const { open: openRecord } = useRecordDetail();

  const deepLinkStateRef = React.useRef<DeepLinkState>(INITIAL_DEEP_LINK_STATE);
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
  // -1 until the member uses j/k, so no focus ring shows on a mouse visit.
  // The keys follow a record, not a position, so a refetch that reorders rows never moves them to another one.
  const [focusedKey, setFocusedKey] = React.useState<string | null>(null);
  const [keysUsed, setKeysUsed] = React.useState(false);
  const [focusMoves, setFocusMoves] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  // The API writes missing summaries one at a time after it answers the first page: read again a few times while
  // some are still missing, then stop until the next visit.
  const pending = summariesPending(rowsQuery.data?.pages[0]?.rows ?? []);
  const [refreshes, setRefreshes] = React.useState({ workspaceId, count: 0 });
  const refreshed = refreshes.workspaceId === workspaceId ? refreshes.count : 0;
  const { refetch } = rowsQuery;
  React.useEffect(() => {
    if (!pending || !workspaceId || refreshed >= SUMMARY_REFRESHES) return;
    const timer = window.setTimeout(() => {
      setRefreshes({ workspaceId, count: refreshed + 1 });
      void refetch();
    }, SUMMARY_REFRESH_MS);
    return () => window.clearTimeout(timer);
  }, [pending, workspaceId, refreshed, refetch]);

  const allRows = React.useMemo(
    () => (rowsQuery.data?.pages ?? []).flatMap((page) => page.rows),
    [rowsQuery.data],
  );
  const needle = query.trim().toLowerCase();
  const matching = needle
    ? allRows.filter((row) => row.name.toLowerCase().includes(needle))
    : allRows;
  // A row read on this visit stays under New until the next one, so it never jumps away while it is open.
  const [readHere, setReadHere] = React.useState<ReadonlySet<string>>(() => new Set());
  const isNew = (row: FeedRowData) => row.unseen === true || readHere.has(feedRecordKey(row));
  const newRows = matching.filter(isNew);
  const earlierRows = matching.filter((row) => !isNew(row));
  const visible = [...newRows, ...earlierRows];

  const markSeen = (row: FeedRowData, how: 'expand' | 'button' | 'key') => {
    if (row.unseen !== true) return;
    setReadHere((prev) => new Set(prev).add(feedRecordKey(row)));
    capture('feed_row_seen', { dataset: row.dataset, seen: true, how });
    seen.markSeen.mutate(row, { onError: () => toast.error("Couldn't mark that row seen.") });
  };
  const toggle = (row: FeedRowData) => {
    const key = feedRecordKey(row);
    const opening = expandedKey !== key;
    setExpandedKey(opening ? key : null);
    if (!opening) return;
    capture('feed_row_opened', { dataset: row.dataset });
    markSeen(row, 'expand');
  };
  const open = (row: FeedRowData, tab?: string) => {
    deepLinkStateRef.current = decideDeepLink(deepLinkStateRef.current, {
      type: 'pageOpen',
    }).state;
    openRecord({ type: row.dataset, id: row.record_id }, { source: 'feed', tab });
  };
  const openSuggestion = (row: { dataset: FeedRowData['dataset']; record_id: string }) => {
    deepLinkStateRef.current = decideDeepLink(deepLinkStateRef.current, {
      type: 'pageOpen',
    }).state;
    openRecord({ type: row.dataset, id: row.record_id }, { source: 'feed' });
  };
  const openSpecChat = useOpenSpecChat();
  const specAvailable = useSpecChatAvailable();
  // One chat per click: a second click while the first is opening would create (and bill) a second chat.
  const asking = React.useRef(false);
  const askSpec = (row: FeedRowData) => {
    if (asking.current) return;
    asking.current = true;
    void openSpecChat({
      starter: askSpecStarter(row),
      title: specChatTitle(row.name),
      context: { kind: 'record', type: row.dataset, id: row.record_id },
    })
      .catch(() => toast.error('Spec could not open that chat.'))
      .finally(() => {
        asking.current = false;
      });
  };

  const focusedIndex = visible.findIndex((row) => feedRecordKey(row) === focusedKey);
  useFeedKeyboard({
    rows: visible,
    focusedIndex,
    armed: keysUsed && focusedIndex >= 0,
    setFocusedIndex: (index) => {
      const row = visible[index];
      if (!row) return;
      setKeysUsed(true);
      setFocusedKey(feedRecordKey(row));
      setFocusMoves((moves) => moves + 1);
    },
    toggle,
    open,
    markRead: (row) => markSeen(row, 'key'),
    focusSearch: () => searchRef.current?.focus(),
    collapse: () => setExpandedKey(null),
  });

  // j or k hands DOM focus to the highlighted row, so Enter opens it and the browser scrolls to it. Only a key move
  // does: a refetch or a search that shifts the row never pulls focus out of what the member is using.
  const focusedKeyRef = React.useRef(focusedKey);
  focusedKeyRef.current = focusedKey;
  React.useEffect(() => {
    const key = focusedKeyRef.current;
    if (focusMoves === 0 || !key) return;
    const rows = listRef.current?.querySelectorAll<HTMLElement>('[data-feed-key]') ?? [];
    Array.from(rows)
      .find((row) => row.dataset.feedKey === key)
      ?.querySelector<HTMLButtonElement>('button[aria-expanded]')
      ?.focus();
  }, [focusMoves]);

  // An alert email's ?project=<id> opens that project's drawer once (see lib/deep-link.ts).
  const deepLinkProjectId = search.project ?? null;
  const deepLinkQuery = useProjectDetail(deepLinkProjectId, workspaceId);
  const applyDeepLink = React.useCallback(
    (event: DeepLinkEvent, resolveRow?: () => ProjectRow) => {
      const { state, action } = decideDeepLink(deepLinkStateRef.current, event);
      deepLinkStateRef.current = state;
      if (action.open && resolveRow) {
        const row = resolveRow();
        openRecord(
          { type: 'project', id: row.id },
          {
            seed: row,
            source: 'deep_link',
            replace: action.clearParam,
            searchPatch: action.clearParam ? { project: undefined } : undefined,
          },
        );
      } else if (action.clearParam) {
        void navigate({
          to: '.',
          search: (prev: Record<string, unknown>) => ({ ...prev, project: undefined }),
          replace: true,
        });
      }
      if (action.toastNotFound) toast.error("That project couldn't be found.");
    },
    [navigate, openRecord],
  );
  React.useEffect(() => {
    applyDeepLink({ type: 'param', id: deepLinkProjectId });
  }, [deepLinkProjectId, applyDeepLink]);
  React.useEffect(() => {
    const project = deepLinkQuery.data?.project;
    if (project && project.id === deepLinkProjectId) {
      applyDeepLink({ type: 'resolved', projectId: project.id }, () => apiProjectToRow(project));
    }
  }, [deepLinkProjectId, deepLinkQuery.data, applyDeepLink]);
  React.useEffect(() => {
    if (deepLinkProjectId && deepLinkQuery.isError) {
      applyDeepLink({ type: 'errored', dead: isDeadDeepLinkStatus(deepLinkQuery.error) });
    }
  }, [deepLinkProjectId, deepLinkQuery.isError, deepLinkQuery.error, applyDeepLink]);

  const stats = statsQuery.data ?? rowsQuery.data?.pages[0]?.stats ?? undefined;
  const firstName = feedFirstName(
    getUserProfileNameSource(
      profileQuery.data,
      getStructuredAuthMetadataProfileFallback(session?.user.user_metadata),
    ),
  );
  const followed = stats ? stats.followed_accounts + stats.followed_projects : null;
  const isLoading =
    isSupabaseConfigured &&
    Boolean(workspaceSlug) &&
    (workspaceQuery.isLoading || rowsQuery.isLoading);

  // The Feed-wide count, not the loaded pages: an unread row can sit on a page not yet loaded.
  const caughtUp =
    !isLoading &&
    allRows.length > 0 &&
    (stats?.unseen_rows != null
      ? stats.unseen_rows === 0
      : allRows.every((row) => row.unseen !== true));

  const renderRow = (row: FeedRowData, index: number) => (
    <FeedRow
      key={feedRecordKey(row)}
      row={row}
      score={scores?.find((s) => s.key === (row.dataset === 'account' ? 'icp' : 'atlas'))}
      index={index}
      expanded={expandedKey === feedRecordKey(row)}
      focused={keysUsed && focusedKey === feedRecordKey(row)}
      onToggle={() => {
        setFocusedKey(feedRecordKey(row));
        toggle(row);
      }}
      onMarkRead={() => markSeen(row, 'button')}
    >
      <FeedRowExpanded
        row={row}
        onOpen={() => open(row)}
        onAskSpec={specAvailable ? () => askSpec(row) : undefined}
        onReviewContacts={() => open(row, 'contacts')}
      />
    </FeedRow>
  );

  return (
    <main className="flex min-w-0 flex-1 justify-center">
      <div className="w-full max-w-[1040px] px-8 pb-16 pt-10">
        <FeedHeader firstName={firstName} stats={stats} caughtUp={caughtUp} />

        <div className="mt-6">
          {isLoading ? (
            <SuggestedSkeleton />
          ) : (
            <FeedSuggested workspaceId={workspaceId} onOpen={openSuggestion} empty={null} />
          )}
        </div>

        <section className="mt-6">
          <div className="mb-3">
            <FeedFilters query={query} onQueryChange={setQuery} searchRef={searchRef} />
          </div>

          {isLoading ? (
            <FeedSkeleton />
          ) : rowsQuery.isError ? (
            <div className="rounded-xl border px-6 py-16 text-center text-sm text-muted-foreground">
              Couldn't load the News feed.{' '}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => void rowsQuery.refetch()}
              >
                Retry
              </button>
            </div>
          ) : followed === 0 ? (
            <FeedEmpty linkFor={linkFor} />
          ) : visible.length === 0 ? (
            <div className="rounded-xl border px-6 py-16 text-center text-sm text-muted-foreground">
              {needle ? 'Nothing matches that search.' : 'No recent news on what you follow.'}
            </div>
          ) : (
            <div ref={listRef} className="space-y-5">
              {newRows.length > 0 ? (
                <ol aria-label="New" className="space-y-2">
                  {newRows.map((row, index) => renderRow(row, index))}
                </ol>
              ) : null}
              {earlierRows.length > 0 ? (
                // The Earlier heading only divides read rows from new ones; alone, the read rows are just the list.
                <FeedSection label={newRows.length > 0 ? 'Earlier' : null}>
                  {earlierRows.map((row, index) => renderRow(row, newRows.length + index))}
                </FeedSection>
              ) : null}
            </div>
          )}

          {rowsQuery.hasNextPage ? (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={rowsQuery.isFetchingNextPage}
                onClick={() => void rowsQuery.fetchNextPage()}
              >
                {rowsQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function FeedSection({ label, children }: { label: string | null; children: React.ReactNode }) {
  return (
    <section aria-label={label ?? 'News feed'}>
      {label ? (
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </h2>
      ) : null}
      <ol className="space-y-2">{children}</ol>
    </section>
  );
}
